"use client";

import { useEffect, useState } from "react";
import { Menu, MoveUpRight } from "lucide-react";

import { cn } from "cn";
import { BrandName } from "@/shared/ui/brand-name";
import { Container } from "@/shared/ui/container";
import { Button } from "@/shared/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shared/ui/sheet";
import type { SiteSettings } from "@/shared/config/site";

const SCROLL_THRESHOLD_PX = 16;

/** Плавающая шапка: прозрачная над hero, после скролла — стеклянная панель. */
export function Header({ settings: siteConfig }: { settings: SiteSettings }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [demoReady, setDemoReady] = useState(false);

  useEffect(() => {
    const ready = () => setDemoReady(true);
    window.addEventListener("seero:office-ready", ready);
    return () => window.removeEventListener("seero:office-ready", ready);
  }, []);

  useEffect(() => {
    let frame = 0;

    const sync = () => {
      frame = 0;
      setScrolled(window.scrollY > SCROLL_THRESHOLD_PX);
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(sync);
    };

    sync();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Стеклянная подложка отдельным слоем: скролл не двигает контент шапки */}
      <div
        aria-hidden="true"
        className={cn(
          "glass pointer-events-none absolute inset-0 transition-opacity duration-300",
          scrolled ? "opacity-100" : "opacity-0"
        )}
      />
      <Container className="relative flex h-16 items-center justify-between gap-6">
        <a
          href="#top"
          className="shrink-0 font-heading text-sm font-semibold tracking-wide text-foreground transition-opacity hover:opacity-80 sm:text-base"
        >
          <BrandName name={siteConfig.name} logo />
        </a>

        <nav
          aria-label="Основная навигация"
          className="hidden items-center gap-5 lg:flex"
        >
          {siteConfig.nav.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={cn(
                "relative text-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:text-foreground",
                "after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0",
                "after:bg-gradient-to-r after:from-primary after:to-transparent after:transition-transform after:duration-300",
                "hover:after:scale-x-100 focus-visible:after:scale-x-100"
              )}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button disabled={!demoReady} onClick={() => window.dispatchEvent(new Event("seero:open-demo"))}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2.5 text-xs text-white transition-colors hover:bg-white/10 disabled:opacity-40">
            Демо <MoveUpRight size={13} />
          </button>

          <Button asChild className="hidden sm:inline-flex">
            <a href="#contact">Обсудить проект</a>
          </Button>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label="Открыть меню"
              >
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[88%] max-w-sm justify-between gap-0"
            >
              <SheetHeader className="px-6 pb-2 pt-6">
                <SheetTitle className="font-heading text-base font-semibold tracking-wide">
                  <BrandName name={siteConfig.name} logo />
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Основная навигация по сайту
                </SheetDescription>
              </SheetHeader>

              <nav
                aria-label="Мобильная навигация"
                className="flex flex-col px-6"
              >
                {siteConfig.nav.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={closeMenu}
                    className="border-b border-foreground/10 py-4 font-heading text-lg text-foreground transition-colors hover:text-primary"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>

              <div className="flex flex-col gap-5 px-6 pb-10 pt-6 mt-auto">
                <a
                  href={siteConfig.contacts.phoneHref}
                  className="text-base text-muted-foreground tabular-nums transition-colors hover:text-foreground"
                >
                  {siteConfig.contacts.phone}
                </a>
                <Button asChild size="lg">
                  <a href="#contact" onClick={closeMenu}>
                    Обсудить проект
                  </a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </Container>
    </header>
  );
}
